import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UserCog, Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/lib/api";

const empty = { name: "", email: "", password: "", role: "staff" };

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get("/staff");
    setStaff(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/staff", form);
      toast.success("Staff added");
      setForm(empty);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const resetPassword = async () => {
    try {
      await api.put(`/staff/${resetFor.id}`, { password: newPassword });
      toast.success("Password updated");
      setResetFor(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="staff-page">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="label-caps mb-2 flex items-center gap-2"><UserCog size={12} /> Access Control</div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Staff & Admins</h1>
          <p className="text-slate-500 mt-1">Manage admin and staff accounts that can access this panel.</p>
        </div>
        <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="add-staff-button">
          <Plus size={16} className="mr-2" /> Add Staff
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps">Name</th>
                <th className="px-4 py-3 label-caps">Email</th>
                <th className="px-4 py-3 label-caps">Role</th>
                <th className="px-4 py-3 label-caps">Created</th>
                <th className="px-4 py-3 label-caps text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`}>
                  <td className="px-4 py-4 font-medium">{s.name}</td>
                  <td className="px-4 py-4 font-mono text-xs">{s.email}</td>
                  <td className="px-4 py-4">
                    <Badge variant="outline" className={s.role === "super_admin" ? "status-paid" : "status-info"}>
                      {s.role === "super_admin" ? "Super Admin" : "Staff"}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500">{formatDate(s.created_at)}</td>
                  <td className="px-4 py-4 text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => { setResetFor(s); setNewPassword(""); }} data-testid={`reset-staff-${s.id}`}>
                      <KeyRound size={14} className="mr-1" /> Password
                    </Button>
                    {s.id !== user?.id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" data-testid={`delete-staff-${s.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove {s.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will revoke their access immediately. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(s.id)} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No staff yet</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add staff */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Staff / Admin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-caps">Full Name</Label><Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11" /></div>
            <div><Label className="label-caps">Email</Label><Input data-testid="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11" /></div>
            <div><Label className="label-caps">Password</Label><Input data-testid="staff-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 h-11 font-mono" placeholder="Min 6 characters" /></div>
            <div>
              <Label className="label-caps">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (limited access)</SelectItem>
                  <SelectItem value="super_admin">Super Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={create} disabled={!form.name || !form.email || !form.password} data-testid="save-staff">Create Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {resetFor?.name}</DialogTitle></DialogHeader>
          <div>
            <Label className="label-caps">New password</Label>
            <Input data-testid="staff-new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-2 h-11 font-mono" placeholder="Min 6 characters" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFor(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={resetPassword} disabled={newPassword.length < 6} data-testid="confirm-reset-password">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
